/* eslint-disable max-lines */
/**
 * Process Visibility Component - API Version
 * Uses secure API endpoints instead of direct Firestore queries
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Grid,
  Alert,
  Tabs,
  Tab,
  Switch
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Circle as CircleIcon,
  FilterList as FilterListIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { formatDistanceToNow, format } from 'date-fns';
import ProcessVisibilitySettings from './ProcessVisibilitySettings';
import ProcessVisibilityAdvancedFilters from './ProcessVisibilityAdvancedFilters';
import { getApiUrl } from '../../config/apiUrls';
import { fetchWithAuth } from '../../services/api';
import { groupConsecutiveSteps, transcriptionMetaLine } from './pvStepDisplay';

// Subsystem phases configuration
const SUBSYSTEMS = {
  ingestion: {
    name: 'Ingestion',
    color: '#1976d2',
    operations: ['folder_scan', 'file_download', 'google_drive_sync', 'gcs_upload'],
    operationDescriptions: {
      'folder_scan': 'Scanning folders for files',
      'file_download': 'Downloading file from Google Drive',
      'google_drive_sync': 'Synchronizing with Google Drive',
      'gcs_upload': 'Uploading file to Cloud Storage'
    }
  },
  ptl: {
    name: 'Privacy Tokenization',
    color: '#9c27b0',
    operations: ['dlp_analysis', 'tokenization', 'dlp_complete', 'tokenization_complete'],
    operationDescriptions: {
      'dlp_analysis': 'Analyzing for sensitive data',
      'tokenization': 'Tokenizing PII/sensitive data',
      'dlp_complete': 'Data Loss Prevention analysis complete',
      'tokenization_complete': 'Tokenization process complete',
      'transcription_dispatched': 'GPU transcription job launched',
      'transcription_in_progress': 'Transcribing media (GPU job running)',
      'transcription_retrying': 'Transcription attempt failed; retrying',
      'transcription_completed': 'Media transcription complete',
      'transcription_failed': 'Transcription permanently failed'
    }
  },
  te: {
    name: 'Entity Discovery',
    color: '#ff6f00',
    operations: ['entity_extraction', 'vertex_ai_analysis', 'entity_discovery', 'discovery_complete'],
    operationDescriptions: {
      'entity_extraction': 'Extracting entities from content',
      'vertex_ai_analysis': 'AI analysis using Vertex AI',
      'entity_discovery': 'Discovering new entities',
      'discovery_complete': 'Entity discovery complete'
    }
  },
  document_mgmt: {
    name: 'Document Management',
    color: '#2e7d32',
    operations: ['template_generation', 'pdf_export', 'document_creation'],
    operationDescriptions: {
      'template_generation': 'Generating document template',
      'pdf_export': 'Exporting to PDF format',
      'document_creation': 'Creating final document'
    }
  }
};

// Status color mapping. Includes BOTH the legacy vocabulary
// (started/progress/success) and the statuses PV actually writes
// (pending/processing/completed/skipped) - previously the real ones
// rendered colorless with 'undefinedXX' style values.
const STATUS_COLORS = {
  started: '#1976d2',
  progress: '#ff9800',
  success: '#4caf50',
  error: '#f44336',
  pending: '#9e9e9e',
  processing: '#ff9800',
  completed: '#4caf50',
  skipped: '#9e9e9e'
};

const STATUS_ICONS = {
  started: HourglassEmptyIcon,
  progress: HourglassEmptyIcon, // Use static icon instead of spinner
  success: CheckCircleIcon,
  error: ErrorIcon,
  pending: HourglassEmptyIcon,
  processing: HourglassEmptyIcon,
  completed: CheckCircleIcon,
  skipped: HourglassEmptyIcon
};


// eslint-disable-next-line max-lines-per-function
function ProcessVisibilityAPI({ entityId }) {
  console.log('[PV API Component] Mounted with entityId:', entityId);
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [filterExpanded, setFilterExpanded] = useState(true); // Show filters by default
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [stageModalOpen, setStageModalOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [error, setError] = useState(null);

  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState(null);

  // Polling state
  const [lastTimestamp, setLastTimestamp] = useState(null);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false); // Disable auto-polling by default
  const [pollInterval] = useState(30000); // 30 seconds default
  const POLL_INTERVAL = pollInterval; // Dynamic polling interval

  // Add refs to prevent re-render loops
  const fetchInProgress = useRef(false);

  /**
   * Fetch process visibility logs from API
   */
  // eslint-disable-next-line max-lines-per-function
  const fetchProcessingLogs = useCallback(async (isPolling = false, filters = {}) => {
    if (!entityId) return;

    // Prevent concurrent fetches
    if (fetchInProgress.current) {
      console.log('[PV API] Fetch already in progress, skipping...');
      return;
    }

    fetchInProgress.current = true;

    const apiUrl = getApiUrl('processVisibility');

    try {
      if (!isPolling) {
        setLoading(true);
      }
      setError(null);

      // Build request payload with advanced filters
      const requestBody = {
        entityId,
        limit: 500,
        ...filters, // Use advanced filters
        ...(isPolling && lastTimestamp && { since: lastTimestamp })
      };

      console.log('[PV API] Request body:', requestBody);
      console.log('[PV API] Filters being applied:', {
        searchTerm: filters.searchTerm,
        fileExtensions: filters.fileExtensions,
        artifactTypes: filters.artifactTypes,
        hasFilters: Object.keys(filters).length > 0
      });


      const data = await fetchWithAuth(`${apiUrl}/api/process-visibility/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });


      // Handle both old 'logs' and new 'documents' format
      const rawDocuments = data.documents || data.logs || [];

      // Ensure rawDocuments is an array
      if (!Array.isArray(rawDocuments)) {
        console.warn('[PV API] rawDocuments is not an array:', rawDocuments);
        setDocuments([]);
        setLoading(false);
        return;
      }

      console.log('[PV API] Received', rawDocuments.length, 'documents from API');
      console.log('[PV API] Filters response:', data.filters);

      // Transform V3 documents to UI format
      const documents = rawDocuments.map(doc => {
        // Check if this is a V3 document (has pipeline object)
        if (doc.pipeline && typeof doc.pipeline === 'object' && doc.pipeline !== null) {
          console.log('[PV API] Processing V3 document:', doc.id, 'pipeline:', doc.pipeline);

          // Helper to convert Firestore timestamp to Date
          const convertTimestamp = (timestamp) => {
            if (!timestamp) return null; // Return null instead of new Date() for empty timestamps

            // Handle Firestore timestamp object with seconds/nanoseconds
            if (timestamp.seconds !== undefined) {
              const date = new Date(timestamp.seconds * 1000);
              return isNaN(date.getTime()) ? null : date;
            }

            // Handle Firestore timestamp with _seconds/_nanoseconds format
            if (timestamp._seconds !== undefined) {
              const date = new Date(timestamp._seconds * 1000);
              return isNaN(date.getTime()) ? null : date;
            }

            // Handle if it's already a Date
            if (timestamp instanceof Date) {
              return isNaN(timestamp.getTime()) ? null : timestamp;
            }

            // Handle if it has toDate method
            if (timestamp.toDate && typeof timestamp.toDate === 'function') {
              try {
                const date = timestamp.toDate();
                return isNaN(date.getTime()) ? null : date;
              } catch (e) {
                console.warn('[PV API] Failed to convert timestamp with toDate:', e);
                return null;
              }
            }

            // Try to parse as number or string
            try {
              const date = new Date(timestamp);
              return isNaN(date.getTime()) ? null : date;
            } catch (e) {
              console.warn('[PV API] Failed to parse timestamp:', timestamp, e);
              return null;
            }
          };

          // Find the most recent pipeline stage
          let latestStage = null;
          let latestTime = 0;
          Object.entries(doc.pipeline).forEach(([subsystem, stage]) => {
            const stageDate = convertTimestamp(stage.lastUpdated);
            if (stageDate && !isNaN(stageDate.getTime())) {
              const stageTime = stageDate.getTime();
              if (stageTime > latestTime) {
                latestTime = stageTime;
                latestStage = { subsystem, ...stage };
              }
            }
          });

          // Transform V3 to UI format
          return {
            // Basic info
            id: doc.id,
            documentId: doc.id,
            entityId: doc.entityId,
            fileName: doc.metadata?.originalFileName || doc.gcsPath?.split('/').pop() || 'Unknown',
            fileSizeBytes: doc.metadata?.fileSizeBytes || 0,
            fileExtension: doc.metadata?.fileExtension || '',

            // Timing
            startTime: convertTimestamp(doc.createdAt),
            endTime: convertTimestamp(doc.lastUpdated),

            // Status
            latestStatus: latestStage ? {
              status: latestStage.status,
              subsystem: latestStage.subsystem,
              timestamp: latestTime > 0 ? new Date(latestTime) : new Date()
            } : null,

            // Pipeline info
            pipeline: doc.pipeline,
            subsystems: new Set(Object.keys(doc.pipeline || {})),

            // V3 specific
            isV3: true,
            metadata: doc.metadata,
            summary: doc.summary,
            searchableTokens: doc.searchableTokens || [],
            gcsPath: doc.gcsPath
          };
        }

        // Return old format as-is
        return doc;
      });

      if (documents.length > 0) {
        console.log('[PV API] First transformed document:', documents[0]);
      }

      // Update last timestamp for polling
      if (data.lastTimestamp) {
        setLastTimestamp(data.lastTimestamp);
      }

      // Update filter options from API response
      if (data.filters && data.filters.available) {
        console.log('[PV API] Available filter options from API:', data.filters.available);
        setFilterOptions(data.filters.available);
      }

      console.log('[PV API] Final transformed documents:', documents.length, documents);

      // If polling and we have new data, merge with existing
      if (isPolling && documents.length > 0) {
        console.log('[PV API] Merging with existing documents (polling)');
        setDocuments(prevDocs => {
          // Merge new documents with existing documents
          const updatedDocuments = mergeDocuments(prevDocs, documents);
          return updatedDocuments;
        });
      } else {
        console.log('[PV API] Setting documents directly:', documents.length);
        // For V3 documents, they're already grouped by document ID
        // No need to group logs anymore
        setDocuments(documents);
      }

      setLastRefresh(new Date());

    } catch (error) {
      console.error('Error fetching process logs:', error);
      console.error('Error stack:', error.stack);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        apiUrl: apiUrl
      });
      setError(error.message || 'Failed to fetch process visibility logs');
    } finally {
      setLoading(false);
      fetchInProgress.current = false; // Reset the flag
    }
  }, [entityId, lastTimestamp]);

  /**
   * Render timeline for V3 documents
   */
  // eslint-disable-next-line max-lines-per-function
  const renderV3DocumentTimeline = (doc) => {
    const convertTimestamp = (timestamp) => {
      if (!timestamp) return null;

      // Handle standard Firestore format
      if (timestamp.seconds !== undefined) {
        return new Date(timestamp.seconds * 1000);
      }

      // Handle _seconds format
      if (timestamp._seconds !== undefined) {
        return new Date(timestamp._seconds * 1000);
      }

      if (timestamp instanceof Date) return timestamp;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      return new Date(timestamp);
    };

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Processing Pipeline (Click stages for details)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 1, overflowX: 'auto', pb: 1 }}>
          {/* eslint-disable-next-line max-lines-per-function */}
          {['ingestion', 'ptl', 'te'].map(subsystem => {
            const stage = doc.pipeline?.[subsystem];
            if (!stage) return null;

            const config = SUBSYSTEMS[subsystem];
            const hasActivity = stage.status !== 'pending';
            const startTime = convertTimestamp(stage.startTime);
            const endTime = convertTimestamp(stage.lastUpdated);
            const duration = startTime && endTime && !isNaN(startTime.getTime()) && !isNaN(endTime.getTime())
              ? endTime.getTime() - startTime.getTime()
              : null;

            return (
              <Paper
                key={subsystem}
                elevation={hasActivity ? 2 : 0}
                onClick={() => handleStageClick(stage, subsystem, doc)}
                sx={{
                  minWidth: 220,
                  p: 2,
                  border: hasActivity ? 'none' : `1px dashed ${theme.palette.divider}`,
                  backgroundColor: hasActivity ? theme.palette.background.paper : theme.palette.action.hover,
                  opacity: hasActivity ? 1 : 0.5,
                  cursor: hasActivity ? 'pointer' : 'default',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': hasActivity ? {
                    elevation: 4,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                  } : {}
                }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ color: config.color, fontWeight: 'bold' }}>
                  {config.name}
                </Typography>

                {hasActivity ? (
                  <Box>
                    <Chip
                      size="small"
                      icon={getStatusIcon(stage.status)}
                      label={stage.status}
                      sx={{
                        mb: 1,
                        backgroundColor: STATUS_COLORS[stage.status] + '20',
                        borderColor: STATUS_COLORS[stage.status],
                        borderWidth: 1,
                        borderStyle: 'solid'
                      }}
                    />

                    {stage.lastOperation && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {config.operationDescriptions?.[stage.lastOperation] || stage.lastOperation}
                      </Typography>
                    )}

                    {/* Timing Information */}
                    <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                      {startTime && !isNaN(startTime.getTime()) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Started: {format(startTime, 'HH:mm:ss')}
                        </Typography>
                      )}
                      {endTime && !isNaN(endTime.getTime()) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Updated: {format(endTime, 'HH:mm:ss')}
                        </Typography>
                      )}
                      {duration && duration >= 0 && (
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 'medium' }}>
                          Duration: {
                            duration < 1000 ? `${duration}ms` :
                            duration < 60000 ? `${(duration / 1000).toFixed(1)}s` :
                            `${Math.floor(duration / 60000)}m ${((duration % 60000) / 1000).toFixed(1)}s`
                          }
                        </Typography>
                      )}
                    </Box>

                    {stage.lastMessage && (
                      <Typography variant="caption" color="text.secondary" sx={{
                        display: 'block',
                        mt: 1,
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {stage.lastMessage}
                      </Typography>
                    )}

                    {/* Subsystem-specific metrics */}
                    {subsystem === 'ingestion' && stage.filesProcessed > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Files: {stage.filesProcessed} ({formatFileSize(stage.bytesProcessed || 0)})
                      </Typography>
                    )}
                    {subsystem === 'ptl' && stage.tokensCreated > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Tokens created: {stage.tokensCreated}
                      </Typography>
                    )}
                    {subsystem === 'te' && (stage.entitiesDiscovered > 0 || stage.relationshipsFound > 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Entities: {stage.entitiesDiscovered || 0}, Relations: {stage.relationshipsFound || 0}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Not processed
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  };

  /**
   * Merge new V3 documents with existing ones (for polling)
   */
  const mergeDocuments = (existingDocs, newDocs) => {
    const docMap = new Map(existingDocs.map(doc => [doc.id, doc]));

    // Update or add new documents
    newDocs.forEach(newDoc => {
      docMap.set(newDoc.id, newDoc);
    });

    // Convert back to array and sort by lastUpdated
    return Array.from(docMap.values()).sort((a, b) => {
      const timeA = a.lastUpdated?.toDate?.() || a.lastUpdated || 0;
      const timeB = b.lastUpdated?.toDate?.() || b.lastUpdated || 0;
      return timeB - timeA; // Newest first
    });
  };


  // Monitor documents state changes
  useEffect(() => {
    console.log('[PV API] Documents state changed:', documents.length, documents);
  }, [documents]);

  // Initial fetch on component mount or entity change
  useEffect(() => {
    console.log('[PV API] Initial fetch effect triggered - entityId:', entityId);
    if (entityId) {
      fetchProcessingLogs(false, {}); // Start with empty filters
    }
  }, [entityId, fetchProcessingLogs]); // Only run when entityId changes

  // Separate effect for polling
  useEffect(() => {
    console.log('[PV API] Polling effect triggered - enabled:', isPollingEnabled);
    let interval;
    if (isPollingEnabled) {
      interval = setInterval(() => {
        console.log('[PV API] Polling interval triggered');
        fetchProcessingLogs(true, advancedFilters);
      }, POLL_INTERVAL);
    }

    return () => {
      if (interval) {
        console.log('[PV API] Clearing polling interval');
        clearInterval(interval);
      }
    };
  }, [isPollingEnabled, POLL_INTERVAL, advancedFilters, fetchProcessingLogs]); // Include all dependencies


  /**
   * Format file size
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  /**
   * Get status icon component
   */
  const getStatusIcon = (status) => {
    const Icon = STATUS_ICONS[status] || CircleIcon;
    // Never show spinning progress for timeline chips - use static icon
    return <Icon style={{ fontSize: 16 }} />;
  };

  /**
   * Handle stage click to open detail modal
   */
  const handleStageClick = (stage, subsystem, doc) => {
    setSelectedStage({
      ...stage,
      subsystem,
      subsystemConfig: SUBSYSTEMS[subsystem],
      documentId: doc.documentId,
      documentName: doc.fileName
    });
    setStageModalOpen(true);
  };

  /**
   * Stage Detail Modal Component
   */
  // eslint-disable-next-line max-lines-per-function
  const renderStageModal = () => {
    if (!selectedStage) return null;


    const convertTimestamp = (timestamp) => {
      if (!timestamp) return null;

      // Handle standard Firestore format
      if (timestamp.seconds !== undefined) {
        return new Date(timestamp.seconds * 1000);
      }

      // Handle _seconds format
      if (timestamp._seconds !== undefined) {
        return new Date(timestamp._seconds * 1000);
      }

      if (timestamp instanceof Date) return timestamp;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      return new Date(timestamp);
    };

    // Extract steps and derive timing information
    const extractStepsAndTiming = (stage) => {
      const steps = [];
      let derivedStartTime = null;

      // Look for various step properties in the stage
      const stepProperties = ['steps', 'operations', 'events', 'log', 'history'];

      for (const prop of stepProperties) {
        if (stage[prop] && Array.isArray(stage[prop])) {
          steps.push(...stage[prop]);
        }
      }

      // Also check for individual timestamp properties that might indicate steps
      Object.keys(stage).forEach(key => {
        if (key.includes('Time') || key.includes('timestamp') || key.includes('At')) {
          const timestamp = convertTimestamp(stage[key]);
          if (timestamp && !isNaN(timestamp.getTime())) {
            steps.push({
              type: key,
              timestamp: timestamp,
              operation: key.replace(/Time|timestamp|At/gi, '') || 'unknown'
            });
          }
        }
      });

      // Sort steps by timestamp to find the earliest
      if (steps.length > 0) {
        steps.sort((a, b) => {
          const timeA = convertTimestamp(a.timestamp || a.time || a.createdAt);
          const timeB = convertTimestamp(b.timestamp || b.time || b.createdAt);
          if (!timeA || !timeB) return 0;
          return timeA.getTime() - timeB.getTime();
        });

        const firstStep = steps[0];
        derivedStartTime = convertTimestamp(firstStep.timestamp || firstStep.time || firstStep.createdAt);
      }

      return { steps, derivedStartTime };
    };

    const { steps, derivedStartTime } = extractStepsAndTiming(selectedStage);

    // Use recorded start time, or fall back to derived start time from first step
    const startTime = convertTimestamp(selectedStage.startTime) || derivedStartTime;
    const endTime = convertTimestamp(selectedStage.lastUpdated);
    const duration = startTime && endTime && !isNaN(startTime.getTime()) && !isNaN(endTime.getTime())
      ? endTime.getTime() - startTime.getTime()
      : null;

    return (
      <Dialog
        open={stageModalOpen}
        onClose={() => setStageModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          backgroundColor: selectedStage.subsystemConfig.color + '20',
          borderLeft: `4px solid ${selectedStage.subsystemConfig.color}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {getStatusIcon(selectedStage.status)}
          <Box>
            <Typography variant="h6" sx={{ color: selectedStage.subsystemConfig.color }}>
              {selectedStage.subsystemConfig.name} Stage Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedStage.documentName} ({selectedStage.documentId})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Stage Status */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Status Information</Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Current Status</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}>
                  {selectedStage.status}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Last Operation</Typography>
                <Typography variant="body2">
                  {selectedStage.subsystemConfig.operationDescriptions?.[selectedStage.lastOperation] || selectedStage.lastOperation || 'None'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Timing Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Timing Information</Typography>
            <Grid container spacing={2}>
              <Grid size={4}>
                <Typography variant="caption" color="text.secondary">Start Time</Typography>
                <Typography variant="body2">
                  {startTime && !isNaN(startTime.getTime())
                    ? format(startTime, 'PPpp')
                    : selectedStage.status === 'pending'
                      ? 'Not started'
                      : 'Started (exact time unknown)'}
                </Typography>
                {startTime && !convertTimestamp(selectedStage.startTime) && derivedStartTime && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                    ⚡ Derived from first step
                  </Typography>
                )}
                {!startTime && selectedStage.status !== 'pending' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Stage has activity but start time wasn't recorded
                  </Typography>
                )}
              </Grid>
              <Grid size={4}>
                <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                <Typography variant="body2">
                  {endTime && !isNaN(endTime.getTime())
                    ? format(endTime, 'PPpp')
                    : selectedStage.status !== 'pending'
                      ? 'Recently active'
                      : 'No activity'}
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {duration && duration >= 0 ? (
                    duration < 1000 ? `${duration}ms` :
                    duration < 60000 ? `${(duration / 1000).toFixed(1)}s` :
                    `${Math.floor(duration / 60000)}m ${((duration % 60000) / 1000).toFixed(1)}s`
                  ) : selectedStage.status === 'pending'
                    ? 'Not started'
                    : 'Active (duration unknown)'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Actual Operations Performed */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Operations Performed</Typography>
            {selectedStage.lastOperation ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  label={selectedStage.subsystemConfig.operationDescriptions?.[selectedStage.lastOperation] || selectedStage.lastOperation}
                  size="small"
                  variant="filled"
                  color="primary"
                  icon={<CheckCircleIcon />}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No operations completed yet
              </Typography>
            )}

            {/* Show operations completed based on status */}
            {selectedStage.status && selectedStage.status !== 'pending' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Stage Status: {selectedStage.status}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedStage.status === 'started' && (
                    <>
                      <HourglassEmptyIcon color="primary" fontSize="small" />
                      <Typography variant="body2">Stage initiated - processing in progress</Typography>
                    </>
                  )}
                  {selectedStage.status === 'progress' && (
                    <>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Active processing - operations in progress</Typography>
                    </>
                  )}
                  {selectedStage.status === 'success' && (
                    <>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2">Stage completed successfully</Typography>
                    </>
                  )}
                  {selectedStage.status === 'error' && (
                    <>
                      <ErrorIcon color="error" fontSize="small" />
                      <Typography variant="body2">Stage encountered an error</Typography>
                    </>
                  )}
                  {!['started', 'progress', 'success', 'error'].includes(selectedStage.status) && (
                    <>
                      <CircleIcon color="action" fontSize="small" />
                      <Typography variant="body2">Status: {selectedStage.status}</Typography>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Individual Steps Performed */}
          {steps && steps.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Steps Performed ({steps.length} {steps.length === 1 ? 'step' : 'steps'})
              </Typography>
              <Box sx={{
                maxHeight: 200,
                overflowY: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1
              }}>
                {groupConsecutiveSteps(steps).map((group, index, groups) => {
                  const { lastStep, count, op } = group;
                  const stepTime = convertTimestamp(lastStep.timestamp || lastStep.time || lastStep.createdAt);
                  const details = lastStep.details || lastStep.message || lastStep.description;
                  const metaLine = transcriptionMetaLine(lastStep.metadata);

                  return (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderBottom: index < groups.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        backgroundColor: index === 0 ? theme.palette.primary.main + '05' : 'transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: index === 0 ? 'bold' : 'normal' }}>
                          {index + 1}. {op}
                          {index === 0 && (
                            <Chip
                              label="First Step"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {count > 1 && (
                            <Tooltip title={`${count} consecutive attempts (all retained in PV)`}>
                              <Chip
                                label={`×${count}`}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stepTime && !isNaN(stepTime.getTime())
                            ? format(stepTime, 'HH:mm:ss')
                            : 'Unknown time'}
                        </Typography>
                      </Box>
                      {details && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2 }}>
                          {details}
                        </Typography>
                      )}
                      {metaLine && (
                        <Typography variant="caption" color="primary" sx={{ display: 'block', ml: 2 }}>
                          {metaLine}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
              {derivedStartTime && (
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                  ⚡ Stage start time derived from first step
                </Typography>
              )}
            </Box>
          )}

          {/* Stage Metadata */}
          {(selectedStage.metadata || selectedStage.details || selectedStage.stepCount) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Stage Details</Typography>
              <Box sx={{
                p: 2,
                backgroundColor: theme.palette.action.hover,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`
              }}>
                {selectedStage.stepCount && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Steps Completed:</strong> {selectedStage.stepCount}
                  </Typography>
                )}
                {selectedStage.metadata && Object.keys(selectedStage.metadata).length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Metadata:
                    </Typography>
                    {Object.entries(selectedStage.metadata).map(([key, value]) => (
                      <Typography key={key} variant="body2" sx={{ ml: 1, mb: 0.5 }}>
                        <strong>{key}:</strong> {String(value)}
                      </Typography>
                    ))}
                  </Box>
                )}
                {selectedStage.details && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Details:</strong> {selectedStage.details}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Error Information */}
          {selectedStage.error && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom color="error">Error Information</Typography>
              <Paper sx={{ p: 2, backgroundColor: theme.palette.error.light + '10' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {typeof selectedStage.error === 'string' ? selectedStage.error : JSON.stringify(selectedStage.error, null, 2)}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Additional Metadata */}
          {selectedStage.metadata && Object.keys(selectedStage.metadata).length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Additional Information</Typography>
              <Paper sx={{ p: 2, backgroundColor: theme.palette.action.hover }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedStage.metadata, null, 2)}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStageModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  /**
   * Render timeline for a document
   */
  // eslint-disable-next-line max-lines-per-function
  const renderDocumentTimeline = (doc) => {
    // Handle V3 documents differently
    if (doc.isV3) {
      return renderV3DocumentTimeline(doc);
    }

    // Original timeline rendering for old format
    // Group logs by subsystem
    const subsystemLogs = {};
    Object.keys(SUBSYSTEMS).forEach(key => {
      subsystemLogs[key] = [];
    });

    // Safety check for logs array
    if (!doc.logs || !Array.isArray(doc.logs)) {
      console.warn('[PV Timeline] Document has no logs array:', doc.fileName);
      return <Typography variant="body2" color="text.secondary">No timeline data available</Typography>;
    }

    // Debug: Show what subsystems are in this document
    const uniqueSubsystems = [...new Set(doc.logs.map(log => log.subsystem))];
    console.log('[PV Timeline] Document:', doc.fileName, 'has subsystems:', uniqueSubsystems);

    doc.logs.forEach(log => {
      // Map the subsystem value to our known subsystems
      const subsystemKey = log.subsystem?.toLowerCase();

      if (subsystemKey && subsystemLogs[subsystemKey]) {
        subsystemLogs[subsystemKey].push(log);
      }
    });

    // Debug: Show distribution after grouping
    console.log('[PV Timeline] Distribution:', {
      ingestion: subsystemLogs.ingestion.length,
      ptl: subsystemLogs.ptl.length,
      te: subsystemLogs.te.length,
      document_mgmt: subsystemLogs.document_mgmt.length
    });

    return (
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', py: 2 }}>
        {/* eslint-disable-next-line max-lines-per-function */}
        {Object.entries(SUBSYSTEMS).map(([key, config]) => {
          const logs = subsystemLogs[key];
          const hasLogs = logs.length > 0;

          return (
            <Paper
              key={key}
              elevation={hasLogs ? 2 : 0}
              sx={{
                minWidth: 200,
                p: 2,
                border: hasLogs ? 'none' : `1px dashed ${theme.palette.divider}`,
                backgroundColor: hasLogs ? theme.palette.background.paper : theme.palette.action.hover,
                opacity: hasLogs ? 1 : 0.5
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ color: config.color, fontWeight: 'bold' }}>
                {config.name}
              </Typography>

              {hasLogs ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {logs.sort((a, b) => {
                    // Use high-precision timestamp if available
                    const preciseA = a.timestampPrecise || (a.timestamp ? a.timestamp.getTime() : 0);
                    const preciseB = b.timestampPrecise || (b.timestamp ? b.timestamp.getTime() : 0);

                    // If high-precision timestamps are very close (within 1ms), use operation logic
                    if (Math.abs(preciseA - preciseB) < 1) {
                      // Logical order of operations
                      const logicalOrder = {
                        'folder_scan': 1,
                        'file_download': 2,
                        'google_drive_sync': 3,
                        'gcs_upload': 4,
                        'sync_file': 5,
                        'ingestion_complete': 6,
                        'dlp_analysis': 10,
                        'tokenization': 11,
                        'dlp_complete': 12,
                        'tokenization_complete': 13,
                        'entity_extraction': 20,
                        'vertex_ai_analysis': 21,
                        'entity_discovery': 22,
                        'discovery_complete': 23
                      };

                      const orderA = logicalOrder[a.operation] || 99;
                      const orderB = logicalOrder[b.operation] || 99;

                      // If same operation, sort by status
                      if (orderA === orderB) {
                        const operationOrder = {
                          'started': 0,
                          'progress': 1,
                          'success': 2,
                          'error': 3
                        };
                        const statusOrderA = operationOrder[a.status] || 999;
                        const statusOrderB = operationOrder[b.status] || 999;
                        return statusOrderA - statusOrderB;
                      }

                      return orderA - orderB;
                    }

                    return preciseA - preciseB;
                  // eslint-disable-next-line max-lines-per-function
                  }).map((log, index, array) => {
                    // Calculate time to next log entry
                    let timeToNext = null;
                    if (index < array.length - 1) {
                      const nextLog = array[index + 1];
                      const timeDiff = nextLog.timestamp.getTime() - log.timestamp.getTime();
                      timeToNext = timeDiff < 1000
                        ? `${timeDiff}ms`
                        : `${(timeDiff / 1000).toFixed(3)}s`;
                    }

                    // Get actual operation duration from processing results
                    const operationDuration = log.logEntry?.processingResults?.durationMs
                      ? log.logEntry.processingResults.durationMs < 1000
                        ? `${log.logEntry.processingResults.durationMs}ms`
                        : `${(log.logEntry.processingResults.durationMs / 1000).toFixed(3)}s`
                      : null;

                    return (
                      <Box key={log.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                {SUBSYSTEMS[log.subsystem]?.operationDescriptions?.[log.operation] || log.operation}
                              </Typography>
                              <Typography variant="caption">{log.message}</Typography>
                              <Typography variant="caption" display="block">
                                {format(log.timestamp, 'PPpp')} ({formatDistanceToNow(log.timestamp)} ago)
                              </Typography>
                              {operationDuration && (
                                <Typography variant="caption" display="block" color="success.main" sx={{ mt: 0.5 }}>
                                  ⏱️ Operation took: {operationDuration}
                                </Typography>
                              )}
                              {timeToNext && (
                                <Typography variant="caption" display="block" color="info.main">
                                  ⏳ Wait to next step: {timeToNext}
                                </Typography>
                              )}
                              {log.logEntry?.processingResults && (
                                <Box sx={{ mt: 0.5 }}>
                                  {log.logEntry.processingResults.itemsProcessed !== undefined && (
                                    <Typography variant="caption" display="block">
                                      Items processed: {log.logEntry.processingResults.itemsProcessed}
                                    </Typography>
                                  )}
                                  {log.logEntry.processingResults.itemsGenerated !== undefined && (
                                    <Typography variant="caption" display="block">
                                      Items generated: {log.logEntry.processingResults.itemsGenerated}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {doc.latestStatus?.timestamp.getTime() === log.timestamp.getTime() && (
                                <Typography variant="caption" display="block" color="primary" fontWeight="bold" sx={{ mt: 0.5 }}>
                                  ← Current Status
                                </Typography>
                              )}
                            </Box>
                          }
                        >
                          <Chip
                            size="small"
                            icon={getStatusIcon(log.status)}
                            label={log.operation}
                            onClick={() => {
                              setSelectedEntry(log);
                              setDetailsOpen(true);
                            }}
                            sx={{
                              backgroundColor: STATUS_COLORS[log.status] + '20',
                              borderColor: STATUS_COLORS[log.status],
                              borderWidth: 1,
                              borderStyle: 'solid',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: STATUS_COLORS[log.status] + '40'
                              }
                            }}
                          />
                        </Tooltip>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center' }}>
                          {operationDuration && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'success.main',
                                fontSize: '0.65rem',
                                lineHeight: 1,
                                fontWeight: 'bold'
                              }}
                            >
                              {operationDuration}
                            </Typography>
                          )}
                          {/* Only show time to next if we have operation duration OR if it's meaningful (>10ms) */}
                          {timeToNext && (operationDuration || parseInt(timeToNext) > 10) && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.65rem',
                                lineHeight: 1
                              }}
                            >
                              ↓ {timeToNext}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Not processed
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TimelineIcon sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Process Visibility (API)
        </Typography>
      </Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          API Error: {error}
        </Alert>
      )}
      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Processing Logs" icon={<TimelineIcon />} iconPosition="start" />
          <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" />
        </Tabs>
      </Paper>
      {/* Tab Content */}
      {activeTab === 0 ? (
        <Box>
          {/* Logs View Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
              {lastRefresh && !isNaN(lastRefresh.getTime()) ? (
                <>Last updated: {format(lastRefresh, 'PPpp')} ({formatDistanceToNow(lastRefresh)} ago)</>
              ) : (
                'Not updated yet'
              )}
              {isPollingEnabled && lastTimestamp && (
                <span> • Auto-refresh enabled</span>
              )}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={isPollingEnabled}
                  onChange={(e) => setIsPollingEnabled(e.target.checked)}
                  size="small"
                />
              }
              label="Auto-refresh"
              sx={{ mr: 2 }}
            />
            <IconButton onClick={() => fetchProcessingLogs(false, advancedFilters)} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={() => setFilterExpanded(!filterExpanded)}>
              {filterExpanded ? <ExpandLessIcon /> : <FilterListIcon />}
            </IconButton>
          </Box>

          {/* Advanced Filters Component */}
          <Collapse in={filterExpanded}>
            <ProcessVisibilityAdvancedFilters
              onFiltersChange={(filters) => {
                console.log('[PV API] Advanced filters changed:', filters);
                setAdvancedFilters(filters);
              }}
              onSearch={(filters) => {
                console.log('[PV API] Advanced search triggered:', filters);
                setAdvancedFilters(filters);
                fetchProcessingLogs(false, filters);
              }}
              filterOptions={filterOptions}
            />
          </Collapse>

          {/* Document List */}
          {(() => {
            console.log('[PV API Render] Documents state:', documents.length, documents);
            console.log('[PV API Render] Loading:', loading, 'Error:', error);
            return null;
          })()}
          {loading && documents.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Alert severity="info">
              No documents found matching the current filters.
              {error && ' Please check the error message above.'}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* eslint-disable-next-line max-lines-per-function */}
              {documents.map(doc => (
                <Paper key={doc.documentId} elevation={1} sx={{ p: 2 }}>
                  {/* Current Status Banner */}
                  {doc.latestStatus && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        backgroundColor: STATUS_COLORS[doc.latestStatus.status] + '10',
                        borderLeft: `4px solid ${STATUS_COLORS[doc.latestStatus.status]}`,
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {getStatusIcon(doc.latestStatus.status)}
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Current Status: {doc.latestStatus.status.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({doc.latestStatus.subsystem} - {doc.latestStatus.timestamp && !isNaN(doc.latestStatus.timestamp.getTime())
                            ? `${formatDistanceToNow(doc.latestStatus.timestamp)} ago`
                            : 'just now'})
                        </Typography>
                      </Box>

                      {/* Process Timing Information */}
                      <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Start Time:</Typography>
                          <Typography variant="body2">
                            {doc.startTime && !isNaN(doc.startTime.getTime())
                              ? format(doc.startTime, 'PPpp')
                              : 'Unknown'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">End Time:</Typography>
                          <Typography variant="body2">
                            {doc.endTime && !isNaN(doc.endTime.getTime())
                              ? format(doc.endTime, 'PPpp')
                              : 'Unknown'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total Duration:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {(() => {
                              // For V3 documents, calculate from pipeline stages
                              if (doc.isV3) {
                                // Validate dates first
                                if (!doc.startTime || !doc.endTime || isNaN(doc.startTime.getTime()) || isNaN(doc.endTime.getTime())) {
                                  return 'Unknown';
                                }

                                // Use wall clock time from start to end
                                const wallClockMs = doc.endTime.getTime() - doc.startTime.getTime();

                                if (wallClockMs < 1000) {
                                  return `${wallClockMs}ms`;
                                } else if (wallClockMs < 60000) {
                                  return `${(wallClockMs / 1000).toFixed(1)}s`;
                                } else {
                                  const minutes = Math.floor(wallClockMs / 60000);
                                  const seconds = ((wallClockMs % 60000) / 1000).toFixed(1);
                                  return `${minutes}m ${seconds}s`;
                                }
                              }

                              // Legacy calculation for old format documents
                              let totalOperationTime = 0;
                              let totalWaitTime = 0;

                              (doc.logs || []).forEach((log, index) => {
                                // Add operation duration if available
                                if (log.logEntry?.processingResults?.durationMs) {
                                  totalOperationTime += log.logEntry.processingResults.durationMs;
                                }

                                // Add wait time to next step
                                if (index < doc.logs.length - 1) {
                                  const nextLog = doc.logs[index + 1];
                                  const waitTime = new Date(nextLog.timestamp).getTime() - new Date(log.timestamp).getTime();
                                  totalWaitTime += waitTime;
                                }
                              });

                              const totalMs = totalOperationTime + totalWaitTime;
                              const wallClockMs = doc.endTime - doc.startTime;

                              // Format total time
                              let formatted;
                              if (totalMs < 1000) {
                                formatted = `${totalMs}ms`;
                              } else if (totalMs < 60000) {
                                formatted = `${(totalMs / 1000).toFixed(3)}s`;
                              } else {
                                const minutes = Math.floor(totalMs / 60000);
                                const seconds = ((totalMs % 60000) / 1000).toFixed(1);
                                formatted = `${minutes}m ${seconds}s`;
                              }

                              // Show breakdown if there's a significant difference or if operations took longer than wall clock
                              if (Math.abs(totalMs - wallClockMs) > 100 || totalOperationTime > wallClockMs) {
                                return (
                                  <Tooltip title={
                                    <Box>
                                      <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        Timing Breakdown:
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Operations total: {(totalOperationTime / 1000).toFixed(3)}s
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Wait between steps: {(totalWaitTime / 1000).toFixed(3)}s
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        Wall clock time: {(wallClockMs / 1000).toFixed(3)}s
                                      </Typography>
                                      {totalOperationTime > wallClockMs && (
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                          Note: Operations ran in parallel or logs were batched
                                        </Typography>
                                      )}
                                    </Box>
                                  }>
                                    <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>{formatted}</span>
                                  </Tooltip>
                                );
                              }

                              return formatted;
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {doc.fileName}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: {doc.documentId}
                        </Typography>
                        <Chip
                          size="small"
                          label={formatFileSize(doc.fileSizeBytes)}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={doc.fileExtension || 'unknown'}
                          variant="outlined"
                        />
                      </Box>

                      {/* GCS Path for V3 documents */}
                      {doc.isV3 && doc.gcsPath && (
                        <Typography variant="caption" color="text.secondary" sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          display: 'block',
                          mt: 0.5,
                          p: 0.5,
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: 0.5,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          📁 {doc.gcsPath}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {doc.latestStatus && (
                        <Chip
                          size="small"
                          icon={getStatusIcon(doc.latestStatus.status)}
                          label={doc.latestStatus.status}
                          sx={{
                            backgroundColor: STATUS_COLORS[doc.latestStatus.status] + '20',
                            borderColor: STATUS_COLORS[doc.latestStatus.status],
                            borderWidth: 1,
                            borderStyle: 'solid'
                          }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {doc.endTime && !isNaN(doc.endTime.getTime())
                          ? `${format(doc.endTime, 'PPpp')} (${formatDistanceToNow(doc.endTime)} ago)`
                          : 'Processing...'}
                      </Typography>
                    </Box>
                  </Box>

                  {renderDocumentTimeline(doc)}
                </Paper>
              ))}
            </Box>
          )}

          {/* Log Entry Details Dialog */}
          <Dialog
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            maxWidth="md"
            fullWidth
          >
            {selectedEntry && (
              <>
                <DialogTitle>
                  Log Entry Details
                  <Chip
                    size="small"
                    icon={getStatusIcon(selectedEntry.status)}
                    label={selectedEntry.status}
                    sx={{
                      ml: 2,
                      backgroundColor: STATUS_COLORS[selectedEntry.status] + '20',
                      borderColor: STATUS_COLORS[selectedEntry.status],
                      borderWidth: 1,
                      borderStyle: 'solid'
                    }}
                  />
                </DialogTitle>
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <Typography variant="caption" color="text.secondary">Document ID</Typography>
                      <Typography variant="body2" gutterBottom>{selectedEntry.documentId}</Typography>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                      <Typography variant="body2" gutterBottom>
                        {format(selectedEntry.timestamp, 'PPpp')} ({formatDistanceToNow(selectedEntry.timestamp)} ago)
                      </Typography>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <Typography variant="caption" color="text.secondary">Subsystem</Typography>
                      <Typography variant="body2" gutterBottom>{selectedEntry.subsystem}</Typography>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <Typography variant="caption" color="text.secondary">Operation</Typography>
                      <Typography variant="body2" gutterBottom>{selectedEntry.operation}</Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Message</Typography>
                      <Typography variant="body2" gutterBottom>{selectedEntry.message}</Typography>
                    </Grid>

                    {selectedEntry.searchableTokens?.length > 0 && (
                      <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Tokens</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {selectedEntry.searchableTokens.map((token, idx) => (
                            <Chip key={idx} size="small" label={token} variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {selectedEntry.searchableKeywords?.length > 0 && (
                      <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Keywords</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {selectedEntry.searchableKeywords.map((keyword, idx) => (
                            <Chip key={idx} size="small" label={keyword} color="primary" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {selectedEntry.logEntry?.processingResults && (
                      <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">Processing Results</Typography>
                        <pre style={{
                          fontSize: '12px',
                          backgroundColor: theme.palette.action.hover,
                          padding: '8px',
                          borderRadius: '4px',
                          overflow: 'auto'
                        }}>
                          {JSON.stringify(selectedEntry.logEntry.processingResults, null, 2)}
                        </pre>
                      </Grid>
                    )}
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Stage Detail Modal */}
          {renderStageModal()}
        </Box>
      ) : (
        // Settings Tab
        (<ProcessVisibilitySettings entityId={entityId} />)
      )}
    </Box>
  );
}

export default ProcessVisibilityAPI;